/**
 * /checkout/success?session_id=cs_test_...
 *
 * Polls /api/checkout/status/{session_id} until payment_status flips to "paid"
 * and fulfillment_state to "fulfilled". The polling is mandated by the Stripe
 * playbook (don't trust the redirect alone).
 */
import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../api";
import { useStudentAuth } from "../StudentAuthContext";

const MAX_ATTEMPTS = 30;
const POLL_MS = 2000;

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { status, token, student } = useStudentAuth();
  const [phase, setPhase] = useState("polling"); // polling | success | timeout | error
  const [details, setDetails] = useState(null);
  const [errMsg, setErrMsg] = useState("");
  const attemptsRef = useRef(0);
  const toastFiredRef = useRef(false);

  const sessionId = params.get("session_id");

  useEffect(() => {
    if (!sessionId) {
      setPhase("error");
      setErrMsg("Missing session_id in URL.");
      return;
    }
    if (status === "anon") {
      navigate("/login", { state: { next: `/checkout/success?session_id=${sessionId}` }, replace: true });
      return;
    }
    if (status !== "auth") return;

    let cancelled = false;
    let timer;

    async function poll() {
      if (cancelled) return;
      attemptsRef.current += 1;
      try {
        const data = await api.checkoutStatus(token, sessionId);
        setDetails(data);
        if (data.fulfillment_state === "fulfilled") {
          setPhase("success");
          if (!toastFiredRef.current) {
            toastFiredRef.current = true;
            toast.success("Booking confirmed", {
              description: "Check your inbox — your invite is on its way.",
              duration: 5000,
            });
          }
          return;
        }
        if (data.fulfillment_state === "failed") {
          setPhase("error");
          setErrMsg("We couldn't finalise your booking after payment. We've been notified — please contact us so we can sort it out.");
          return;
        }
        if (data.status === "expired") {
          setPhase("error");
          setErrMsg("Your payment session expired. Please try again.");
          return;
        }
      } catch (err) {
        // 404 = unknown session, stop immediately. 401/403 = auth — also stop.
        const msg = String(err.message || "");
        if (/\b(404|403|401)\b/.test(msg) || /not found/i.test(msg) || /not your/i.test(msg)) {
          setPhase("error");
          setErrMsg(msg || "Payment session not found.");
          return;
        }
        // Any other error: keep polling — the webhook will eventually update the doc.
      }
      if (attemptsRef.current >= MAX_ATTEMPTS) {
        setPhase("timeout");
        return;
      }
      timer = setTimeout(poll, POLL_MS);
    }
    poll();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [sessionId, status, token, navigate]);

  return (
    <main className="min-h-[100dvh] bg-[#FAF9F6] pt-24 md:pt-32 pb-16 px-6" data-testid="checkout-success-page">
      <div className="max-w-md mx-auto">
        {phase === "polling" && (
          <>
            <div className="text-[10px] tracking-[0.32em] uppercase font-medium text-[#1C1C1C]/60 mb-4">
              Confirming
            </div>
            <h1 className="font-display text-4xl md:text-5xl leading-tight tracking-tighter font-light text-[#1C1C1C]">
              Securing your booking…
            </h1>
            <p className="mt-4 text-base text-[#1C1C1C]/70">
              We're confirming your payment and creating your virtual classrooms.
              This usually takes a few seconds.
            </p>
          </>
        )}

        {phase === "success" && (
          <>
            <div className="text-[10px] tracking-[0.32em] uppercase font-medium text-[#1C1C1C]/60 mb-4">
              Confirmed
            </div>
            <h1 className="font-display text-4xl md:text-5xl leading-tight tracking-tighter font-light text-[#1C1C1C]">
              ¡Hecho! You're booked.
            </h1>
            <p className="mt-4 text-base text-[#1C1C1C]/70 leading-relaxed">
              We've sent a confirmation email{student?.email ? (
                <> to <span className="font-medium text-[#1C1C1C]">{student.email}</span></>
              ) : null} with each lesson's join link and a calendar attachment.
            </p>
            <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#1C1C1C]/5 text-[11px] tracking-[0.18em] uppercase text-[#1C1C1C]/65" data-testid="checkout-success-email-hint">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
              Email arriving shortly
            </div>
            {details && (
              <div className="mt-8 p-5 bg-white rounded-2xl border border-[#1C1C1C]/10" data-testid="checkout-success-details">
                <div className="text-[10px] tracking-[0.32em] uppercase text-[#1C1C1C]/55 mb-1">Total paid</div>
                <div className="font-display text-3xl text-[#1C1C1C]">
                  £{Number(details.amount_total).toFixed(2)}
                </div>
                <div className="text-xs text-[#1C1C1C]/50 mt-2">
                  {details.lesson_ids?.length || 0} lesson{(details.lesson_ids?.length || 0) === 1 ? "" : "s"} created
                </div>
              </div>
            )}
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/me"
                className="px-5 py-3 bg-[#1C1C1C] text-[#FAF9F6] rounded-xl text-sm tracking-[0.18em] uppercase font-medium hover:bg-[#1C1C1C]/85 transition-colors"
                data-testid="checkout-success-me-link"
              >
                My lessons →
              </Link>
              <Link
                to="/book"
                className="px-5 py-3 bg-white border border-[#1C1C1C]/15 text-[#1C1C1C] rounded-xl text-sm tracking-[0.18em] uppercase font-medium hover:border-[#1C1C1C]/40 transition-colors"
              >
                Book more
              </Link>
            </div>
          </>
        )}

        {phase === "timeout" && (
          <>
            <h1 className="font-display text-4xl md:text-5xl leading-tight tracking-tighter font-light text-[#1C1C1C]">
              Hmm, taking a while.
            </h1>
            <p className="mt-4 text-base text-[#1C1C1C]/70">
              Your payment may still be processing. Check your email shortly —
              if nothing arrives, contact us.
            </p>
          </>
        )}

        {phase === "error" && (
          <>
            <h1 className="font-display text-4xl md:text-5xl leading-tight tracking-tighter font-light text-[#1C1C1C]">
              Something went wrong.
            </h1>
            <p className="mt-4 text-base text-[#B83A3A]" data-testid="checkout-success-error">
              {errMsg}
            </p>
          </>
        )}
      </div>
    </main>
  );
}
